import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";

export const errText = (e, fallback = "Something went wrong. Please try again.") => {
  const d = e?.response?.data?.detail;
  if (!d) return fallback;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" ");
  return fallback;
};

export const STATUS_LABEL = {
  pending_payment: "Awaiting payment",
  checkout_error: "Checkout failed",
  in_review: "Customization in review",
  scheduled: "Confirmed",
  live: "Live now",
  completed: "Completed",
};

export const StatusBadge = ({ status, testid }) => {
  const tone = {
    scheduled: "bg-[#e7efe4] text-[#24502a] border-[#b9d0b3]",
    live: "bg-[#7a1010] text-white border-[#7a1010]",
    completed: "bg-[#16130f] text-white border-[#16130f]",
  }[status] || "bg-[#f1ece4] text-[#5a524b] border-[#ddd5c9]";
  return (
    <span data-testid={testid} className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold border rounded-full ${tone}`}>
      {STATUS_LABEL[status] || status}
    </span>
  );
};

export const Btn = ({ variant = "primary", className = "", ...p }) => {
  const v = variant === "primary"
    ? "bg-[#7a1010] text-white hover:bg-[#5e0c0c]"
    : variant === "dark"
      ? "bg-[#16130f] text-white hover:bg-[#2b251f]"
      : "bg-transparent text-[#16130f] border border-[#16130f] hover:bg-[#16130f] hover:text-white";
  return (
    <button {...p}
      className={`ent-btn inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold rounded-md transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${v} ${className}`} />
  );
};

export const BtnLink = ({ to, variant = "primary", className = "", children, ...p }) => {
  const v = variant === "primary"
    ? "bg-[#7a1010] text-white hover:bg-[#5e0c0c]"
    : "bg-transparent text-[#16130f] border border-[#16130f] hover:bg-[#16130f] hover:text-white";
  return (
    <Link to={to} {...p}
      className={`ent-btn inline-flex items-center justify-center gap-2 px-5 py-3 text-sm font-semibold rounded-md transition-colors duration-200 ${v} ${className}`}>
      {children}
    </Link>
  );
};

export const Field = ({ id, label, hint, children }) => (
  <div className="space-y-1.5">
    <label htmlFor={id} className="block text-sm font-semibold text-[#16130f]">{label}</label>
    {children}
    {hint && <p id={`${id}-hint`} className="text-xs text-[#5a524b]">{hint}</p>}
  </div>
);

export const inputCls = "w-full rounded-md border border-[#bdb3a6] bg-white px-3 py-2.5 text-[15px] text-[#16130f] placeholder:text-[#8a8178] focus:border-[#7a1010]";

export const Kicker = ({ children }) => (
  <div className="text-xs font-semibold tracking-[0.18em] uppercase text-[#7a1010]">{children}</div>
);

const Header = () => {
  const { user } = useAuth();
  return (
    <header className="border-b border-[#ddd5c9] bg-[#f7f4ef]/95 backdrop-blur sticky top-0 z-30">
      <nav aria-label="Enterprise" className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between gap-4">
        <Link to="/enterprise" className="flex items-baseline gap-2" data-testid="ent-nav-home">
          <span className="font-display text-xl text-[#16130f]">wasntme<span className="text-[#7a1010]">.party</span></span>
          <span className="hidden sm:inline text-xs font-semibold tracking-[0.18em] uppercase text-[#5a524b]">Enterprise</span>
        </Link>
        <div className="flex items-center gap-1 sm:gap-3 text-sm">
          <a href="/enterprise#programs" className="hidden md:inline px-3 py-2 hover:text-[#7a1010]" data-testid="ent-nav-programs">Mission Lab</a>
          <Link to="/enterprise/proposal" className="hidden sm:inline px-3 py-2 hover:text-[#7a1010]" data-testid="ent-nav-proposal">Request a proposal</Link>
          <Link to="/e" className="px-3 py-2 hover:text-[#7a1010]" data-testid="ent-nav-join">Join an event</Link>
          <Link to={user ? "/enterprise/dashboard" : "/enterprise/login"}
            className="px-4 py-2 rounded-md bg-[#16130f] text-white hover:bg-[#2b251f]" data-testid="ent-nav-account">
            {user ? "Dashboard" : "Sign in"}
          </Link>
        </div>
      </nav>
    </header>
  );
};

const Footer = () => (
  <footer className="border-t border-[#ddd5c9] mt-24" data-testid="ent-footer">
    <div className="max-w-6xl mx-auto px-6 py-10 grid md:grid-cols-3 gap-8 text-sm text-[#5a524b]">
      <div>
        <div className="font-display text-lg text-[#16130f]">wasntme<span className="text-[#7a1010]">.party</span></div>
        <p className="mt-2">All-digital interactive mystery experiences. Personalized Enterprise Experiences, built around your team.</p>
        <p className="mt-3 text-xs font-semibold tracking-[0.18em] uppercase text-[#16130f]" data-testid="ent-veteran-owned">U.S. Marine Corps Veteran-Owned Business</p>
      </div>
      <div className="space-y-1">
        <Link to="/enterprise#privacy" className="block hover:text-[#16130f]">Privacy &amp; data handling</Link>
        <Link to="/privacy" className="block hover:text-[#16130f]">Privacy policy</Link>
        <Link to="/terms" className="block hover:text-[#16130f]">Terms</Link>
        <Link to="/" className="block hover:text-[#16130f]">Consumer experiences</Link>
      </div>
      <p>Programs are fictional learning experiences. They are not certifications, operational simulations, or a substitute for specialized training.</p>
    </div>
  </footer>
);

export const EntShell = ({ children, bare = false }) => (
  <div className="ent min-h-screen bg-[#f7f4ef] text-[#16130f]" data-testid="ent-shell">
    <a href="#ent-main" className="sr-only focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:z-50 bg-white px-3 py-2 rounded">Skip to content</a>
    {!bare && <Header />}
    <main id="ent-main" tabIndex={-1}>{children}</main>
    {!bare && <Footer />}
  </div>
);

export const useEntMe = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [me, setMe] = useState(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/enterprise/me", { withCredentials: true });
      setMe(r.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        navigate(`/enterprise/login?next=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
      }
    }
  }, [navigate, location.pathname, location.search]);
  useEffect(() => { load(); }, [load]);
  return { me, reload: load };
};
